import { ProductAttribute, ProductDefinition } from '@/types/productDefinition';

export const TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID = 'tape.customMaterialColor';
export const TAPE_APLA_COLOR_DEFAULT = '#dfbd97';

const normalizeOptionText = (value: unknown) =>
	typeof value === 'string' || typeof value === 'number'
		? String(value)
				.trim()
				.toLowerCase()
				.normalize('NFD')
				.replace(/ł/g, 'l')
				.replace(/[\u0300-\u036f]/g, '')
		: '';

export const isTapeAplaOptionValue = (value: unknown, label?: unknown) => {
	const candidates = [value, label].map(normalizeOptionText).filter(Boolean);
	return candidates.some((candidate) => candidate === 'apla');
};

const findMaterialColorAttribute = (definition: ProductDefinition): ProductAttribute | null => {
	const byFunctional = Object.values(definition.attributes).find(
		(attr) => (attr.functionalId || attr.id) === 'materialColor'
	);
	if (byFunctional) return byFunctional;

	return (
		Object.values(definition.attributes).find((attr) => {
			const label = normalizeOptionText(attr.label);
			return label === 'kolory' || label === 'kolor' || label === 'kolor-materialu';
		}) ?? null
	);
};

/**
 * Tapes expose "Apla" as a material color option but (unlike cartons' "Własny kolor")
 * the API does not ship a follow-up color attribute. Inject a free color picker that
 * becomes visible when Apla is selected.
 */
export const ensureTapeAplaColorAttribute = (
	definition: ProductDefinition | null | undefined
): ProductDefinition | null => {
	if (!definition) return null;

	const existing = definition.attributes[TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID];
	const materialColorAttr = findMaterialColorAttribute(definition);
	if (!materialColorAttr?.options?.length) {
		return definition;
	}

	const aplaOption = materialColorAttr.options.find((option) =>
		isTapeAplaOptionValue(option.value, option.label)
	);
	if (!aplaOption) {
		return definition;
	}

	const aplaValue = aplaOption.value;
	// Prefer functionalId so visibility still works when configuration is keyed
	// by materialColor (lifecycle / handleValueChange aliases).
	const visibilityAttributeId = materialColorAttr.functionalId || materialColorAttr.id;
	const visibilityConditions = [
		{
			attributeId: visibilityAttributeId,
			values: [aplaValue],
		},
	];

	const colorAttribute: ProductAttribute = {
		...(existing ?? {}),
		id: TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
		functionalId: TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
		originalId: existing?.originalId ?? TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID,
		label: existing?.label ?? 'Kolor apli',
		description: existing?.description ?? 'Wybierz dowolny kolor apli',
		control: 'color',
		defaultValue: existing?.defaultValue ?? TAPE_APLA_COLOR_DEFAULT,
		required: false,
		visibilityConditions: existing?.visibilityConditions?.length
			? existing.visibilityConditions
			: visibilityConditions,
	};

	const attributes = {
		...definition.attributes,
		[TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID]: colorAttribute,
	};

	const attributesOrder = [...definition.attributesOrder];
	if (!attributesOrder.includes(TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID)) {
		const materialIndex = attributesOrder.indexOf(materialColorAttr.id);
		if (materialIndex >= 0) {
			attributesOrder.splice(materialIndex + 1, 0, TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID);
		} else {
			attributesOrder.push(TAPE_CUSTOM_MATERIAL_COLOR_ATTRIBUTE_ID);
		}
	}

	return {
		...definition,
		attributes,
		attributesOrder,
	};
};
